import { useEffect } from "react";
import { messaging, getToken, auth } from "../../../config/firebase";
import { registerPushToken } from "../../../api/pushTokenApi";
import * as Sentry from "@sentry/react";

const PUBLIC_VAPID_KEY =
  "BGjvDfCQSJcbUwgR1W_cEqEqqwvfgoZa0D94ANN7lYFxlGdBuTOMuFmOeEHXqDSyE5WNKLlgYHfmu91WGLtFtWA";

let push401WarnedThisSession = false;

const isNativeWrapper = () =>
  typeof window !== "undefined" && Boolean(window.ReactNativeWebView);

const parseIncomingExpoPayload = (parsed) => {
  if (parsed?.type === "EXPO_TOKEN_DATA" && parsed?.data?.trimmedToken) {
    return parsed.data;
  }

  if (
    parsed?.type === "RESPONSE" &&
    parsed?.action === "GET_EXPO_TOKEN_DATA" &&
    parsed?.success &&
    parsed?.data?.trimmedToken
  ) {
    return parsed.data;
  }

  return null;
};

const requestExpoTokenPayload = ({ timeoutMs = 8000 } = {}) =>
  new Promise((resolve, reject) => {
    if (!isNativeWrapper()) {
      reject(new Error("Not running in app wrapper"));
      return;
    }

    let settled = false;
    const finish = (payload, error) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("wrapperResponse", onWrapperResponse);
      clearTimeout(timer);
      if (payload?.trimmedToken) {
        resolve(payload);
      } else {
        reject(error || new Error("Expo token payload not ready"));
      }
    };

    const apply = (parsed) => {
      const payload = parseIncomingExpoPayload(parsed);
      if (payload) {
        finish(payload);
      }
    };

    const onMessage = (event) => {
      if (!event?.data || typeof event.data !== "string") {
        return;
      }
      try {
        apply(JSON.parse(event.data));
      } catch {
        // Ignore non-JSON messages from other parts of the app.
      }
    };

    const onWrapperResponse = (event) => {
      apply(event?.detail);
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("wrapperResponse", onWrapperResponse);

    if (window.sendMessageToWrapper) {
      window.sendMessageToWrapper("GET_EXPO_TOKEN_DATA", {});
    } else {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "REQUEST",
          action: "GET_EXPO_TOKEN_DATA",
          requestId: `expo-token-${Date.now()}`,
          data: {},
        })
      );
    }

    const timer = setTimeout(() => {
      finish(null, new Error("GET_EXPO_TOKEN_DATA timeout"));
    }, timeoutMs);
  });

const connectExpoTokenToUser = async (user, email) => {
  const payload = await requestExpoTokenPayload();
  const trimmedToken = payload.trimmedToken;
  const tokenData =
    payload.tokenData && typeof payload.tokenData === "object"
      ? payload.tokenData
      : {};

  console.log("🔔 PushToken: Connecting Expo native token to user", {
    tokenPreview: `${trimmedToken.slice(0, 20)}...`,
    userId: user.uid,
    userEmail: user.email || email || null,
  });

  await registerPushToken({
    token: trimmedToken,
    userEmail: user.email || email || null,
    platform: tokenData.platform || "unknown",
    timezone: tokenData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  console.log("🔔 PushToken: Expo token connected to userId", user.uid);

  Sentry.logger.info("🔔 PushToken: Expo token connected to userId" + user.uid, {
    log_source: "sentry_test",
  });

  return trimmedToken;
};

/**
 * Hook to register and connect push tokens with the logged-in user's UID
 * This ensures that push tokens in the pushTokens collection are associated with the user
 */
export function usePushTokenRegistration(userId, email) {
  useEffect(() => {
    if (!userId) {
      console.log("🔔 PushToken: Skipping registration (no userId)");
      return;
    }

    const registerAndConnectToken = async () => {
      try {
        console.log("🔔 PushToken: Registration flow started YES", {
          userId,
          email: email || null,
        });

        const user = auth.currentUser;

        if (!user) {
          console.log(
            "🔔 PushToken: Skipping registration (no auth.currentUser)"
          );
          return;
        }

        if (isNativeWrapper()) {
          try {
            await connectExpoTokenToUser(user, email);
          } catch (nativeError) {
            console.warn(
              "🔔 PushToken: Could not connect Expo native token yet",
              nativeError?.message || nativeError
            );
          }
          return;
        }

        if (!messaging) {
          console.log(
            "🔔 PushToken: Skipping registration (messaging unavailable)"
          );
          return;
        }

        let permission;
        try {
          permission = await Notification.requestPermission();
        } catch (error) {
          console.log("pushtoken failed " + error);
        }
        console.log("🔔 PushToken: Notification permission result", {
          permission,
        });
        if (permission !== "granted") {
          console.log("🔔 PushToken: Permission not granted, skipping");
          return;
        }

        let token;
        try {
          token = await getToken(messaging, { vapidKey: PUBLIC_VAPID_KEY });
          console.log("🔔 PushToken: FCM token received", {
            tokenPreview: token ? `${token.slice(0, 12)}...` : null,
            uid: user.uid,
          });
        } catch (tokenError) {
          const code = String(tokenError?.code || "");
          const message = String(tokenError?.message || "");
          const is401 =
            message.includes("401") ||
            tokenError?.serverResponse?.status === 401;
          const isSwError =
            code.includes("failed-service-worker-registration") ||
            code.includes("messaging/failed-service-worker");
          const isAuthError =
            is401 ||
            code.includes("token-subscribe-failed") ||
            message.includes("unauthorized") ||
            message.includes("authentication credential") ||
            message.includes("Expected OAuth") ||
            message.includes("permission-denied") ||
            code.includes("permission-denied");

          if (isSwError) {
            console.warn(
              "🔔 Push notifications unavailable: service worker could not be registered. Deploy a valid firebase-messaging-sw.js and run prebuild."
            );
            return;
          }
          if (isAuthError) {
            if (!push401WarnedThisSession) {
              push401WarnedThisSession = true;
              console.warn(
                "🔔 Push registration failed (401). Check: (1) VAPID key in Firebase Console > Project Settings > Cloud Messaging > Web Push matches PUBLIC_VAPID_KEY for this project, (2) API key unrestricted in Google Cloud, (3) try another browser. App works without push."
              );
            }
            return;
          }
          throw tokenError;
        }

        if (!token) {
          console.log("🔔 PushToken: Empty token, skipping");
          return;
        }

        console.log("🔔 PushToken: Registering token with backend", {
          tokenPreview: `${token.slice(0, 12)}...`,
          userId: user.uid,
          userEmail: user.email || email || null,
        });
        await registerPushToken({
          token,
          platform: "web",
          userEmail: user.email || email || null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        console.log("🔔 PushToken: Token registered successfully");
      } catch (error) {
        const code = String(error?.code || "");
        const message = String(error?.message || "");
        const is401 =
          message.includes("401") || error?.serverResponse?.status === 401;
        const isSwError =
          code.includes("failed-service-worker-registration") ||
          code.includes("messaging/failed-service-worker");
        const isAuthError =
          is401 ||
          code.includes("token-subscribe-failed") ||
          message.includes("unauthorized") ||
          message.includes("authentication credential") ||
          message.includes("Expected OAuth") ||
          message.includes("permission-denied");

        if (isSwError) {
          console.warn(
            "🔔 Push notifications unavailable: service worker could not be registered."
          );
          return;
        }
        if (isAuthError) {
          if (!push401WarnedThisSession) {
            push401WarnedThisSession = true;
            console.warn(
              "🔔 Push registration failed (401). Verify VAPID key and API key for this Firebase project."
            );
          }
          return;
        }
        console.error(
          "🔔 PushToken: Error registering/enriching token:",
          error
        );
      }
    };

    registerAndConnectToken();

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.uid === userId) {
        console.log(
          "🔔 PushToken: Auth state changed for current user, re-running registration"
        );
        registerAndConnectToken();
      }
    });

    const handleLateExpoToken = (event) => {
      if (!isNativeWrapper()) {
        return;
      }
      const user = auth.currentUser;
      if (!user || user.uid !== userId) {
        return;
      }

      let parsed = event?.data;
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          return;
        }
      }

      const payload = parseIncomingExpoPayload(parsed);
      if (!payload?.trimmedToken) {
        return;
      }

      const tokenData =
        payload.tokenData && typeof payload.tokenData === "object"
          ? payload.tokenData
          : {};

      registerPushToken({
        token: payload.trimmedToken,
        userEmail: user.email || email || null,
        platform: tokenData.platform || "unknown",
        timezone: tokenData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).catch((error) => {
        console.warn(
          "🔔 PushToken: Late Expo token connect failed",
          error?.message || error
        );
      });
    };

    window.addEventListener("message", handleLateExpoToken);

    return () => {
      unsubscribe();
      window.removeEventListener("message", handleLateExpoToken);
    };
  }, [userId, email]);
}
